import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Sparkles, PartyPopper, X } from "lucide-react";
import api from "../lib/api";
import { useT } from "../lib/i18n";

// Live-progress onboarding for a freshly provisioned tenant. Self-hides when the
// tenant is not in onboarding (existing tenants) or the owner dismissed it.
const STEP_META = {
  company_profile: { label: "Complete Company Profile", route: "/master-data?tab=Company Profile", showCount: false },
  branches: { label: "Create First Branch", route: "/master-data?tab=Branches", showCount: true },
  suppliers: { label: "Add Supplier", route: "/master-data?tab=Suppliers", showCount: true },
  categories: { label: "Create Product Categories", route: "/master-data?tab=Categories", showCount: true },
  products: { label: "Create Products", route: "/master-data?tab=Products", showCount: true },
  menu_items: { label: "Create Menu", route: "/recipes", showCount: true },
  recipes: { label: "Create Recipes", route: "/recipes", showCount: true },
  employees: { label: "Invite Employees", route: "/hr/employees", showCount: true },
};

export default function OnboardingChecklist() {
  const { t } = useT();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(() => {
    api.get("/onboarding/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ active: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const dismiss = useCallback(async () => {
    setHidden(true);
    try { await api.post("/onboarding/dismiss"); } catch { /* best-effort */ }
  }, []);

  // When completed, celebrate briefly then auto-hide (persist dismissal).
  useEffect(() => {
    if (status?.active && status?.completed && !status?.dismissed && !hidden) {
      const timer = setTimeout(() => { dismiss(); }, 6000);
      return () => clearTimeout(timer);
    }
  }, [status, hidden, dismiss]);

  if (hidden || !status || !status.active || status.dismissed) return null;

  if (status.completed) {
    return (
      <div className="mb-6 animate-fade-up overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white p-6 shadow-card" data-testid="onboarding-complete">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-sm">
            <PartyPopper size={28} />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading text-xl font-extrabold text-ink">{t("Congratulations!")}</h2>
            <p className="mt-1 text-sm text-ink-soft">{t("Your Central Flow workspace is now ready for daily operations.")}</p>
          </div>
          <button onClick={dismiss} data-testid="onboarding-complete-close" className="ml-auto rounded-xl border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50">
            {t("Start operating")}
          </button>
        </div>
      </div>
    );
  }

  const pct = status.progress_pct || 0;
  return (
    <div className="mb-6 animate-fade-up overflow-hidden rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-amber-50 to-white p-6 shadow-card" data-testid="onboarding-card">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-sm">
          <Sparkles size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-xl font-extrabold text-ink" data-testid="onboarding-title">{t("Welcome to Central Flow")}</h2>
          <p className="mt-0.5 text-sm text-ink-soft">{t("Your restaurant setup is almost ready. Complete these steps to get started.")}</p>
        </div>
        <button onClick={dismiss} data-testid="onboarding-dismiss" title={t("Skip onboarding")}
          className="shrink-0 rounded-lg p-1.5 text-ink-mute transition-colors hover:bg-white/70 hover:text-ink">
          <X size={18} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="mb-5" data-testid="onboarding-progress">
        <div className="mb-1.5 flex items-center justify-between text-xs font-bold">
          <span className="text-ink-soft">{status.done_count}/{status.total} {t("steps completed")}</span>
          <span className="text-primary" data-testid="onboarding-progress-pct">{pct}%</span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/70">
          <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {status.steps.map((s) => {
          const meta = STEP_META[s.key] || { label: s.key, route: "/", showCount: true };
          return (
            <div key={s.key} data-testid={`onboarding-step-${s.key}`}
              className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${s.done ? "border-emerald-200 bg-emerald-50/60" : "border-line bg-white/70"}`}>
              {s.done
                ? <CheckCircle2 size={20} className="shrink-0 text-emerald-500" />
                : <Circle size={20} className="shrink-0 text-ink-mute" />}
              <div className="min-w-0 flex-1">
                <p className={`truncate text-sm font-semibold ${s.done ? "text-emerald-800" : "text-ink"}`}>{t(meta.label)}</p>
                {meta.showCount && (
                  <p className="text-[11px] font-bold uppercase tracking-wide text-ink-mute" data-testid={`onboarding-count-${s.key}`}>
                    {s.done ? `${s.count}` : `${s.count} / ${s.target}`}
                  </p>
                )}
              </div>
              {!s.done && (
                <button onClick={() => navigate(meta.route)} data-testid={`onboarding-cta-${s.key}`}
                  className="inline-flex shrink-0 items-center gap-1 rounded-xl bg-primary px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-primary-hover">
                  {t("Start")} <ArrowRight size={13} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
